import { useEffect, useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';

/**
 * Tracks the signed-in user's block list and exposes block/unblock helpers.
 * Both `iBlocked` (people I blocked) and `blockedMe` (people who blocked me)
 * are surfaced so DM entry points can hide either direction.
 */
export const useUserBlocks = () => {
	const { user } = useAuth();
	const [iBlocked, setIBlocked] = useState<Set<string>>(new Set());
	const [blockedMe, setBlockedMe] = useState<Set<string>>(new Set());
	const [loading, setLoading] = useState(false);

	const load = useCallback(async () => {
		if (!user) {
			setIBlocked(new Set());
			setBlockedMe(new Set());
			return;
		}
		setLoading(true);
		const [mine, theirs] = await Promise.all([
			supabase.from('user_blocks').select('blocked_id').eq('blocker_id', user.id),
			supabase.from('user_blocks').select('blocker_id').eq('blocked_id', user.id),
		]);
		setIBlocked(new Set((mine.data ?? []).map((r: any) => r.blocked_id)));
		setBlockedMe(new Set((theirs.data ?? []).map((r: any) => r.blocker_id)));
		setLoading(false);
	}, [user]);

	useEffect(() => {
		load();
	}, [load]);

	const block = useCallback(
		async (otherUserId: string) => {
			if (!user) throw new Error('Not signed in');
			const { error } = await supabase
				.from('user_blocks')
				.insert({ blocker_id: user.id, blocked_id: otherUserId });
			if (error && !/duplicate/i.test(error.message)) throw error;
			setIBlocked((prev) => new Set(prev).add(otherUserId));
		},
		[user]
	);

	const unblock = useCallback(
		async (otherUserId: string) => {
			if (!user) throw new Error('Not signed in');
			const { error } = await supabase
				.from('user_blocks')
				.delete()
				.eq('blocker_id', user.id)
				.eq('blocked_id', otherUserId);
			if (error) throw error;
			setIBlocked((prev) => {
				const next = new Set(prev);
				next.delete(otherUserId);
				return next;
			});
		},
		[user]
	);

	const isBlockedEitherWay = useCallback(
		(otherUserId: string | null | undefined) =>
			!!otherUserId && (iBlocked.has(otherUserId) || blockedMe.has(otherUserId)),
		[iBlocked, blockedMe]
	);

	return { iBlocked, blockedMe, loading, block, unblock, isBlockedEitherWay, reload: load };
};
